import mitt from 'mitt';
import { CLOCK_SKEW, DEFAULT_EXPIRES_EARLY_WARNING, LoggerNames } from '../lib/constants';
import { ClientConfig } from '../types';
import { Logger } from '../lib/logger';
import { V2Router } from '../router/v2.router';
import {
  AccessToken,
  IdToken,
  ITokenManager,
  ITokenStorage,
  RefreshToken,
  SSOTokens,
  TokenEvents,
} from './token-manager.types';
import { SSO_TOKEN_KEYS } from '../storage-manager/storage.const';
import { StorageProvider } from '../storage-manager/storage.types';
import { createTokenObjectFromJwt, transformTokenResponse } from './token-manager.utils';
import { AuthSdkError } from '../lib/auth-sdk-error';
import { TransactionManager } from '../transaction/transaction-manager';

interface TokenManagerConfig {
  config: ClientConfig;
  tokenStorage: StorageProvider<ITokenStorage>;
  router: V2Router;
}

export class TokenManager implements ITokenManager {
  private ssoConfig: ClientConfig;
  private emitter = mitt<TokenEvents>();
  private readonly v2Router: V2Router;
  private readonly tokenStorage: StorageProvider<ITokenStorage>;
  private readonly logger: Logger = Logger.createLogger(LoggerNames.TOKEN_MANAGER);
  private renewTimeout: ReturnType<typeof setTimeout> | null = null;
  private expiringWarningInMs: number = DEFAULT_EXPIRES_EARLY_WARNING;
  private expiredTimeout: ReturnType<typeof setTimeout> | null = null;
  private transactionmanager: TransactionManager;
  /**
   * store a reference to any ongoing renewal requests.
   * This helps dedupe any duplicate renewal requests initiated by the user.
   * @private
   */
  private _renewalRequest: Promise<SSOTokens> | null = null;

  /**
   * Controls whether auto-renew is enabled.
   * If true, any time the tokens are updated in the token manager, a new timer
   * will be set to renew the tokens before they expire.
   * @private
   */
  private started = false;

  constructor({ config, router, tokenStorage }: TokenManagerConfig) {
    this.ssoConfig = config;
    this.tokenStorage = tokenStorage;
    this.v2Router = router;
    this.transactionmanager = TransactionManager.getSingleton();
  }

  get accessToken(): AccessToken | null {
    const tokens = this.getTokens();
    if (!tokens) {
      return null;
    }
    return tokens.accessToken ?? null;
  }

  get idToken(): IdToken | null {
    const tokens = this.getTokens();
    if (!tokens) {
      return null;
    }
    return tokens.idToken ?? null;
  }

  get refreshToken(): RefreshToken | null {
    const tokens = this.getTokens();
    return tokens?.refreshToken ?? null;
  }

  /**
   * See {@link ITokenManager#on}
   */
  on<TEvent extends keyof TokenEvents>(event: TEvent, handler: (data: TokenEvents[TEvent]) => void) {
    this.emitter.on(event, handler);
    return () => this.emitter.off(event, handler);
  }

  /**
   * See {@link ITokenManager#off}
   */
  off<TEvent extends keyof TokenEvents>(event: TEvent, handler: (data: TokenEvents[TEvent]) => void) {
    this.emitter.off(event, handler);
  }

  /**
   * See {@link ITokenManager#setTokens}
   */
  setTokens(tokenResponse: SSOTokens) {
    // Values are stored individually because of cookie size limitations.
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.ACCESS_TOKEN, tokenResponse.accessToken?.token);
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.ID_TOKEN, tokenResponse.idToken?.token);
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.REFRESH_TOKEN, tokenResponse.refreshToken?.token);
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.PENDING_REMOVAL, tokenResponse.pendingRemoval);
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.ISSUER, tokenResponse.accessToken?.issuer);
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.SCOPES, tokenResponse.accessToken?.scopes);
    this.tokenStorage.setItem(SSO_TOKEN_KEYS.TOKEN_USE, tokenResponse.accessToken?.tokenUse);
    this.emitter.emit('added', tokenResponse);
  }

  /**
   * See {@link ITokenManager#getTokens}
   */
  getTokens(): SSOTokens | null {
    try {
      const issuer = this.tokenStorage.getItem(SSO_TOKEN_KEYS.ISSUER) ?? this.ssoConfig.issuer;
      const scopes = this.tokenStorage.getItem(SSO_TOKEN_KEYS.SCOPES) ?? [];
      const tokenUse = this.tokenStorage.getItem(SSO_TOKEN_KEYS.TOKEN_USE);
      const accessToken = createTokenObjectFromJwt(this.tokenStorage.getItem(SSO_TOKEN_KEYS.ACCESS_TOKEN), {
        type: 'access',
        issuer,
        scopes,
        tokenUse,
      });
      const idToken = createTokenObjectFromJwt(this.tokenStorage.getItem(SSO_TOKEN_KEYS.ID_TOKEN), {
        type: 'id',
        issuer,
        scopes,
      });
      const refreshToken = createTokenObjectFromJwt(this.tokenStorage.getItem(SSO_TOKEN_KEYS.REFRESH_TOKEN), {
        type: 'refresh',
        issuer,
      });
      const pendingRemoval = this.tokenStorage.getItem(SSO_TOKEN_KEYS.PENDING_REMOVAL);

      return {
        accessToken,
        idToken,
        refreshToken,
        pendingRemoval,
      };
    } catch (e) {
      this.logger.debug('Error getting tokens from storage', e);
      return null;
    }
  }

  /**
   * See {@link ITokenManager#clear}
   */
  clear() {
    const tokens = this.getTokens();
    Object.values(SSO_TOKEN_KEYS).forEach((key) => this.tokenStorage.removeItem(key));
    this.emitter.emit('removed', tokens);
  }

  /**
   * See {@link ITokenManager#start}
   */
  start() {
    this.logger.debug(`Starting TokenManager`);
    this.clearPendingRemovalTokens();
    // Check for existing tokens in the token manager.
    // If they exist, create renewal timers for event pubsub.
    const tokens = this.getTokens();
    if (tokens) {
      this.createRenewalTimers(tokens);
    }
    // we handle these in events so that sync storage events will trigger them as well
    this.on('added', (tokens) => this.createRenewalTimers(tokens));
    this.on('removed', () => this.clearTimeouts());
    this.started = true;
  }

  /**
   * See {@link ITokenManager#stop}
   */
  stop() {
    this.logger.debug('Stopping token manager');
    this.clearTimeouts();
    this.started = false;
  }

  /**
   * See {@link ITokenManager#renew}
   * @param tokens
   */
  async renew(tokens: SSOTokens | null = this.getTokens()): Promise<null | SSOTokens> {
    if (!tokens) {
      this.logger.warn('No tokens found in storage. Cannot renew tokens');
      this.emitter.emit('renew-error', new Error('No tokens found in the storage. Cannot renew tokens'));
      return null;
    }

    // We store this request so that we don't make multiple renewal requests
    // on accident. This could mostly happen if the user chooses to manually
    // call renew while an auto-renewal request is already in progress. By storing and
    // resolving the in-flight promise, we can ensure that all duped calls receive the same
    // response.
    const inFlight = this.transactionmanager.get<SSOTokens>('renew');
    if (inFlight !== null) {
      this.logger.debug('Duplicate renew request detected. Resolving with existing renewal request.');
      return await inFlight;
    }

    return await this.transactionmanager.create('renew', async () => {
      try {
        const newTokens = await this.v2Router
          .token({
            grant_type: 'refresh_token',
            refresh_token: tokens.refreshToken?.token ?? '',
            client_id: this.ssoConfig.clientId,
            redirect_uri: this.ssoConfig.redirectUri,
          })
          .then((tokens) => transformTokenResponse(tokens, this.ssoConfig.issuer));
        this.setTokens(newTokens);
        this.logger.debug('Tokens renewed');
        this.emitter.emit('renewed', newTokens);
        return newTokens;
      } catch (e) {
        this.logger.error('Error renewing tokens', e);
        this.emitter.emit('renew-error', e as Error);
        this.clear();
        return null;
      }
    });
  }

  /**
   * See {@link ITokenManager#hasExpired}
   */
  hasExpired(token: AccessToken | IdToken | null = this.accessToken) {
    if (!token) return true;
    const now = Date.now() + CLOCK_SKEW;
    return token.expiresAt <= now;
  }

  /**
   * Creates two timers for watching token lifetime.
   * - The first timer will emit the `expiring` event when the token is within the buffer period of expiring (10 minutes).
   *
   * - The second timer will emit the `expired` event when the token has expired.
   *
   * @param tokens
   * @private
   */
  private createRenewalTimers(tokens: SSOTokens) {
    if (!tokens.accessToken) {
      this.logger.warn('No access token found. Cannot set renewal timers');
      return;
    }
    const expireTime = new Date(tokens.accessToken.expiresAt);
    // Make sure that a timer is never negative by taking the max of the timeout and 0
    const expiredTimeoutMs = Math.max(expireTime.getTime() - Date.now(), 0);
    const renewalTimeoutMs = Math.max(expiredTimeoutMs - this.expiringWarningInMs, 0);

    if (this.hasExpired(tokens.accessToken)) {
      this.logger.warn('Access token is already expired. Cannot set renewal timers');
      this.emitter.emit('expired', tokens);
      return;
    }

    // Be a good citizen and clean up any old timers
    this.clearTimeouts();
    this.logger.debug(`Renewal timer set for ${new Date(Date.now() + renewalTimeoutMs)}`);
    this.renewTimeout = setTimeout(() => this.emitter.emit('expiring', tokens), renewalTimeoutMs);
    this.logger.debug(`Expired timer set for ${new Date(Date.now() + expiredTimeoutMs)}`);
    this.expiredTimeout = setTimeout(() => this.emitter.emit('expired', tokens), expiredTimeoutMs);
  }

  /**
   * Clears any existing renewal timers.
   * @private
   */
  private clearTimeouts() {
    this.logger.debug('Clearing token renew and expiration timeouts');
    // setTimeout will never return 0, so it is a safe default for clearTimeout
    // https://html.spec.whatwg.org/multipage/timers-and-user-prompts.html#timer-initialisation-steps
    clearTimeout(this.renewTimeout ?? 0);
    clearTimeout(this.expiredTimeout ?? 0);
    this.renewTimeout = null;
    this.expiredTimeout = null;
  }

  /**
   * See {@link ITokenManager#emitError}
   * @internal
   * @param error
   */
  public emitError(error: AuthSdkError) {
    this.emitter.emit('renew-error', error);
  }

  /**
   * See {@link ITokenManager#emit}
   * @internal
   */
  public emit(event: keyof TokenEvents, payload: TokenEvents[typeof event]) {
    this.emitter.emit(event, payload);
  }

  /**
   * See {@link ITokenManager#hasStarted}
   */
  public hasStarted(): boolean {
    return this.started;
  }

  /**
   * See {@link ITokenManager#setStorage}
   * @internal
   */
  public setStorage(payload: ITokenStorage) {
    this.tokenStorage.setStorage(payload);
    // This is for cross-tab sync since some browsers can have issues syncing changes
    // to local storage
    this.emitter.emit('set-storage', this.tokenStorage.getStorage());
  }

  /**
   * See {@link ITokenManager#addPendingRemovalFlags}
   * @internal
   */
  public addPendingRemovalFlags() {
    const tokens: SSOTokens = this.getTokens() ?? {};
    tokens.pendingRemoval = true;
    this.setTokens(tokens);
  }

  /**
   * See {@link ITokenManager#clearPendingRemovalTokens}
   * @internal
   */
  private clearPendingRemovalTokens() {
    const tokens: SSOTokens = this.getTokens() ?? {};
    if (tokens.pendingRemoval) {
      this.logger.debug('Clearing pending removal tokens');
      this.clear();
      this.clearTimeouts();
    }
  }
}
